import type { HotspotType } from "@/types";
import {
  Building2,
  CalendarCheck,
  CalendarRange,
  Circle,
  Contact,
  Crosshair,
  DoorOpen,
  Headphones,
  Image,
  Images,
  Inbox,
  Info,
  Link2,
  ListVideo,
  Map,
  MapPin,
  MonitorPlay,
  Navigation2,
  Settings,
  Signpost,
  Square,
  UserPlus,
  Video,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";

/**
 * Single source of truth: UI concept → lucide icon.
 *
 * A concept that appears on more than one surface (viewer HUD, editor section,
 * sidebar nav, .tap catalog) MUST resolve its icon here so the same thing wears
 * the same glyph everywhere. Before hand-picking a lucide import for a
 * user-facing feature, check for a key here first.
 *
 * The calendar family stays deliberately distinct — three time-shaped concepts
 * that must NOT share a glyph: booking (reserve a slot), opening hours (weekly
 * schedule), closed dates (vacation/shutdown).
 */
export const conceptIcon = {
  // .360 viewer + editor
  autoplay: MonitorPlay, // "Modo Vitrina" / showroom toggle
  autoplayPlaylist: ListVideo, // the playlist sub-feature only
  hotspots: Crosshair, // the markers concept: HUD button + editor sidebar
  rooms: DoorOpen,
  roomConnect: Link2, // bulk reciprocal nav between two rooms
  scenes: Images, // galería de fotos 360 — Camera se confundía con "capturar"
  settings: Settings,

  // shared cross-product
  booking: CalendarCheck,
  bookDemo: CalendarCheck, // "Agendar demo" CTA (landings, navbar, footer, .tap catalog) — same reserve-a-slot gesture as booking
  openHouse: CalendarRange, // scheduled public event (doors open on a date)
  leadCapture: UserPlus, // the capture CTA/action
  leadInbox: Inbox, // the leads list/nav
  contact: Contact, // CRM contact / saved vCard
  property: Building2,

  // .tap
} satisfies Record<string, LucideIcon>;

/**
 * Canonical hotspot-type → icon. The single source every surface reads:
 * ViewerSidebar legend, HotspotListSection editor, and the add-hotspot picker
 * (useHotspotTools). Import this instead of re-declaring the map.
 *
 * Each type keeps a distinct glyph so the picker (adjacent Nav/NavArrow/
 * NavPreview and Video/VideoIframe choices) stays legible; the sidebar/editor
 * lists inherit the same glyphs for free.
 */
export const hotspotTypeIcon: Record<HotspotType, LucideIcon> = {
  Nav: Circle, // "Navegación circular" — floor nav ring
  NavArrow: Navigation2,
  NavSign: Signpost,
  NavPreview: MapPin,
  Info: Info,
  Area: Square,
  Audio: Headphones,
  Video: Video,
  VideoIframe: MonitorPlay,
  Img: Image,
  ImgIframe: Image, // "Imagen embebida"
  FloorMap: Map,
};
